if (!customElements.get('release-mobile-menu')) {
  class ReleaseMobileMenu extends HTMLElement {
    constructor() {
      super();

      this.tabs = [];
      this.panels = [];
      this.activeIndex = 0;
      this.defaultIndex = 0;
      this.startX = 0;
      this.startY = 0;
      this.resizeObserver = null;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';
      this.tabs = Array.from(this.querySelectorAll('[data-release-mobile-menu-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-release-mobile-menu-panel]'));
      this.viewport = this.querySelector('[data-release-mobile-menu-viewport]');
      this.track = this.querySelector('[data-release-mobile-menu-track]');
      this.defaultIndex = this.getValidIndex(Number(this.dataset.defaultIndex || 0));

      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this.setActiveIndex(Number(tab.dataset.index), true);
        });
      });

      if (this.viewport) {
        this.viewport.addEventListener('touchstart', event => this.onTouchStart(event), { passive: true });
        this.viewport.addEventListener('touchend', event => this.onTouchEnd(event), { passive: true });
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeight());
        this.panels.forEach(panel => this.resizeObserver.observe(panel));
      }

      this.setActiveIndex(this.defaultIndex, false);
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    resetToDefault() {
      this.setActiveIndex(this.defaultIndex, false);

      if (this.viewport) {
        this.viewport.scrollTop = 0;
      }
    }

    getValidIndex(index) {
      if (!Number.isFinite(index)) {
        return 0;
      }

      return Math.min(Math.max(index, 0), Math.max(this.panels.length - 1, 0));
    }

    setActiveIndex(index, shouldAnimate) {
      const nextIndex = this.getValidIndex(index);
      this.activeIndex = nextIndex;

      this.style.setProperty('--release-mobile-menu-index', nextIndex);

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === nextIndex;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === nextIndex;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (isActive) {
          panel.removeAttribute('inert');
        } else {
          panel.setAttribute('inert', '');
        }
      });

      window.requestAnimationFrame(() => {
        this.updateHeight();
        this.scrollActiveTabIntoView(shouldAnimate);
      });
    }

    updateHeight() {
      if (!this.viewport || !this.panels[this.activeIndex]) {
        return;
      }

      this.viewport.style.setProperty(
        '--release-mobile-menu-panel-height',
        `${this.panels[this.activeIndex].scrollHeight}px`
      );
    }

    scrollActiveTabIntoView(shouldAnimate) {
      const activeTab = this.tabs[this.activeIndex];

      if (!activeTab) {
        return;
      }

      activeTab.scrollIntoView({
        block: 'nearest',
        inline: 'center',
        behavior: shouldAnimate ? 'smooth' : 'auto'
      });
    }

    onTouchStart(event) {
      if (event.touches.length !== 1) {
        return;
      }

      this.startX = event.touches[0].clientX;
      this.startY = event.touches[0].clientY;
    }

    onTouchEnd(event) {
      if (!event.changedTouches.length) {
        return;
      }

      const endX = event.changedTouches[0].clientX;
      const endY = event.changedTouches[0].clientY;
      const deltaX = endX - this.startX;
      const deltaY = endY - this.startY;
      const isHorizontalSwipe = Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3;

      if (!isHorizontalSwipe) {
        return;
      }

      if (deltaX < 0) {
        this.setActiveIndex(this.activeIndex + 1, true);
      } else {
        this.setActiveIndex(this.activeIndex - 1, true);
      }
    }
  }

  customElements.define('release-mobile-menu', ReleaseMobileMenu);
}

document.addEventListener('toggle', event => {
  const details = event.target;

  if (!details.matches?.('.header__drawer > .drawer__container') || !details.open) {
    return;
  }

  const mobileMenu = details.querySelector('release-mobile-menu');

  if (mobileMenu?.resetToDefault) {
    mobileMenu.resetToDefault();
  }
}, true);
